import { useCallback, useEffect, useMemo, useState } from 'react';
import { useClub } from '../ClubContext';
import {
    getClubTutorialCompletedKey,
    getClubTutorialStorageKey,
} from '../constants/clubTutorialLessons';

function readCompletedSteps(clubId) {
    const key = getClubTutorialStorageKey(clubId);
    if (!key) return [];
    try {
        const saved = localStorage.getItem(key);
        if (!saved) return [];
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export function useClubTutorialProgress(lessonIds = []) {
    const { club } = useClub();
    const clubId = club?.id;
    const [completedSteps, setCompletedSteps] = useState(() => readCompletedSteps(clubId));

    const syncFromStorage = useCallback(() => {
        setCompletedSteps(readCompletedSteps(clubId));
    }, [clubId]);

    useEffect(() => {
        syncFromStorage();
        window.addEventListener('storage', syncFromStorage);
        return () => window.removeEventListener('storage', syncFromStorage);
    }, [syncFromStorage]);

    const completeStep = useCallback(
        stepId => {
            const storageKey = getClubTutorialStorageKey(clubId);
            if (!storageKey) return;

            setCompletedSteps(prev => {
                if (prev.includes(stepId)) return prev;
                const updated = [...prev, stepId];
                localStorage.setItem(storageKey, JSON.stringify(updated));

                const allDone = lessonIds.length > 0 && lessonIds.every(id => updated.includes(id));
                if (allDone) {
                    const completedKey = getClubTutorialCompletedKey(clubId);
                    if (completedKey) {
                        localStorage.setItem(completedKey, 'true');
                    }
                }

                window.dispatchEvent(new Event('storage'));
                return updated;
            });
        },
        [clubId, lessonIds],
    );

    const isCompleted = useCallback(stepId => completedSteps.includes(stepId), [completedSteps]);

    const progress = useMemo(() => {
        if (!lessonIds.length) return 0;
        const done = lessonIds.filter(id => completedSteps.includes(id)).length;
        return Math.round((done / lessonIds.length) * 100);
    }, [completedSteps, lessonIds]);

    const completedCount = useMemo(
        () => lessonIds.filter(id => completedSteps.includes(id)).length,
        [completedSteps, lessonIds],
    );

    const pendingCount = lessonIds.length - completedCount;
    const allCompleted = lessonIds.length > 0 && pendingCount === 0;

    return {
        completedSteps,
        completeStep,
        isCompleted,
        progress,
        completedCount,
        pendingCount,
        allCompleted,
        total: lessonIds.length,
    };
}
